/** Factual context from applied plays. This is not the invite detector or an AI commentator. */
import type { GameEvent, RoomSnapshot, Side } from '@clutch/core';
import { sideName } from '../room/derive';
import { gameElapsed } from './momentum';

export type GameStory = {
  kind: 'run' | 'lead' | 'tie' | 'pressure' | 'player';
  headline: string;
  detail: string;
  side?: Side;
  event: GameEvent;
  /** Actual contributing events; used to keep context stable between ordinary plays. */
  evidence: number[];
};

const scores = (e: GameEvent) => (e.type === 'shot' || e.type === 'freethrow') && e.made === true && !!e.side && (e.points ?? 0) > 0;
const personKey = (e: GameEvent) => e.personId ? `id:${e.personId}` : e.playerName ? `name:${e.playerName}` : undefined;

export function gameStory(
  s: Pick<RoomSnapshot, 'meta' | 'state'>,
  plays: readonly GameEvent[],
  complete: boolean,
): GameStory | undefined {
  if (s.state.status !== 'live' || s.state.clockSec === 0) return undefined;
  // Last version wins after a feed correction; never include unapplied/future events.
  const applied = [...new Map(plays.filter((e) => e.seq <= s.state.asOfSeq).map((e) => [e.seq, e])).values()].sort((a, b) => a.seq - b.seq);
  const end = gameElapsed(s.state.period, s.state.clockSec);
  const scoring = applied.filter(scores);
  const last = scoring.at(-1);
  if (!last || last.period !== s.state.period || end - gameElapsed(last.period, last.clockSec) > 45) return undefined;
  if (last.homeScore !== s.state.homeScore || last.awayScore !== s.state.awayScore) return undefined;
  const side = last.side!;
  const team = sideName(s, side);
  const margin = (last.homeScore - last.awayScore) * (side === 'home' ? 1 : -1);
  const before = margin - last.points!;
  const lastName = last.playerName;
  const detail = lastName ? `${lastName} ${last.points === 3 ? 'hits the three' : last.type === 'freethrow' ? 'at the line' : 'gets the basket'}.` : `${last.points} points on the latest scoring play.`;
  const late = last.period >= 4 && last.clockSec <= 120;

  if (margin > 0 && before <= 0 && (scoring.length > 1 || last.homeScore + last.awayScore > last.points!)) {
    return { kind: 'lead', headline: `${team} take the lead`, detail: `${detail} Now up ${margin}.`, side, event: last, evidence: [last.seq] };
  }
  if (margin === 0 && late) return { kind: 'tie', headline: 'All square. Every play matters.', detail, side, event: last, evidence: [last.seq] };

  const run: GameEvent[] = [];
  let preceding: GameEvent | undefined;
  let closed = complete && applied[0]?.seq === 0;
  for (let i = scoring.length - 1; i >= 0; i--) {
    const e = scoring[i]!;
    if (e.side !== side) { closed = true; preceding = e; break; }
    run.unshift(e);
  }
  const points = run.reduce((n, e) => n + e.points!, 0);
  const runStart = run[0];
  const ownScore = (e: GameEvent | undefined) => (side === 'home' ? e?.homeScore : e?.awayScore) ?? 0;
  const otherScore = (e: GameEvent | undefined) => (side === 'home' ? e?.awayScore : e?.homeScore) ?? 0;
  const scoresAgree = ownScore(last) - ownScore(preceding) === points && otherScore(last) === otherScore(preceding);
  if (closed && scoresAgree && runStart && points >= 6 && end - gameElapsed(runStart.period, runStart.clockSec) <= 240) {
    const contributors = new Map<string, { name: string; points: number }>();
    for (const e of run) {
      const key = personKey(e);
      if (!key || !e.playerName) continue;
      const p = contributors.get(key) ?? { name: e.playerName, points: 0 };
      p.points += e.points!;
      contributors.set(key, p);
    }
    const top = [...contributors.values()].sort((a, b) => b.points - a.points)[0];
    return {
      kind: 'run', headline: `${team} · ${points}–0 run`,
      detail: top && top.points >= 4 ? `${top.name} has ${top.points} of those ${points}.` : `${points} unanswered points.`,
      side, event: last, evidence: run.map((e) => e.seq),
    };
  }

  if (late && Math.abs(margin) <= 3) return { kind: 'pressure', headline: margin === 0 ? 'All square. Every play matters.' : 'One possession can change this.', detail, side, event: last, evidence: [last.seq] };

  const key = personKey(last);
  if (key && lastName) {
    const recent = scoring.filter((e) => e.side === side && personKey(e) === key && end - gameElapsed(e.period, e.clockSec) <= 120);
    const points = recent.reduce((n, e) => n + e.points!, 0);
    const oldest = applied[0];
    const coversWindow = complete || !!oldest && gameElapsed(oldest.period, oldest.clockSec) <= end - 120;
    if (coversWindow && points >= 6) return { kind: 'player', headline: `${lastName} is heating up`, detail: `${points} points in the last two minutes.`, side, event: last, evidence: recent.map((e) => e.seq) };
  }
  return undefined;
}

/** Reserve emphasis for meaningful live arrivals, never every free throw or a historical backfill. */
export function majorPlay(e: GameEvent): boolean {
  const closeLate = e.period >= 4 && e.clockSec <= 120 && Math.abs(e.homeScore - e.awayScore) <= 5;
  return e.type === 'shot' && (e.made === true && (e.points === 3 || /dunk/i.test(e.description)) || closeLate)
    || e.type === 'freethrow' && closeLate && e.made === false;
}
